import React, { useState } from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu, {
  KuikaDropdownMenuItemType
} from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";

const SIMPLE: KuikaDropdownMenuItemType[] = [
  { key: "open", label: "Open", icon: <KuikaSvg name="eye" width={14} height={14} /> },
  { key: "duplicate", label: "Duplicate", icon: <KuikaSvg name="copy" width={14} height={14} /> },
  { key: "rename", label: "Rename" },
  { key: "delete", label: "Delete", icon: <KuikaSvg name="trash" width={14} height={14} />, danger: true }
];

const NESTED: KuikaDropdownMenuItemType[] = [
  { key: "share", label: "Share" },
  {
    key: "export",
    label: "Export",
    children: [
      { key: "pdf", label: "PDF" },
      { key: "png", label: "PNG" },
      { key: "json", label: "JSON" }
    ]
  },
  { key: "settings", label: "Settings", rightSlot: "⌘," }
];

const SCREENS: KuikaDropdownMenuItemType[] = Array.from(
  { length: 24 },
  (_: unknown, index: number): KuikaDropdownMenuItemType => ({
    key: `screen-${index + 1}`,
    label: `Screen ${index + 1}`,
    disabled: index === 3
  })
);

interface IWithTriggerProps {
  options: KuikaDropdownMenuItemType[];
  trigger?: "click" | "hover";
  size?: "md" | "lg";
}

const WithTrigger: React.FC<IWithTriggerProps> = ({ options, trigger, size }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<string | number | null>(null);
  return (
    <KuikaDropdownMenu
      options={options}
      open={open}
      setOpen={setOpen}
      selected={selected}
      setSelected={setSelected}
      trigger={trigger}
      size={size}
    >
      <KuikaButton type="secondary" size="md">
        {selected ? String(selected) : "Open menu"}
      </KuikaButton>
    </KuikaDropdownMenu>
  );
};

export const DropdownMenuStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Dropdown Menu"
      componentName="KuikaDropdownMenu"
      sourcePath="ui-kit/kuika-dropdown-menu"
      description="Menu surface for actions and selection. Supports nested groups, dividers, icons, right-slot shortcuts and danger items."
    >
      <SpecRow label="Trigger · size">
        <WithTrigger options={SIMPLE} trigger="click" size="md" />
        <WithTrigger options={SIMPLE} trigger="hover" size="md" />
        <WithTrigger options={SIMPLE} trigger="click" size="lg" />
      </SpecRow>
      <SpecRow label="Nested submenu" caption="children → opens secondary surface">
        <WithTrigger options={NESTED} trigger="click" size="md" />
      </SpecRow>
      <SpecRow
        label="Long list"
        caption="the highlight slides row to row (pointer or ↑/↓) and scrolls with them; row 4 is disabled"
      >
        <WithTrigger options={SCREENS} trigger="click" size="lg" />
      </SpecRow>
      <SpecRow label="With header + footer" caption="header / footer slots">
        <KuikaDropdownMenu
          options={SIMPLE}
          header={<div style={{ padding: "8px 12px", fontWeight: 600 }}>Bulk actions</div>}
          footer={<div style={{ padding: "8px 12px", color: "var(--lab-sublabel)" }}>{SIMPLE.length} actions</div>}
        >
          <KuikaButton type="secondary">Surface</KuikaButton>
        </KuikaDropdownMenu>
      </SpecRow>
    </ShowcaseFrame>
  );
};
